<template>
  <div class="permissions-page">
    <div class="permissions-page__top">
      <div class="permissions-page__title">Permissions</div>
      <div class="permissions-page__top-actions">
        <el-input
          :prefix-icon="Search"
          v-model="searchQuery"
          placeholder="Search"
        />

        <el-dropdown trigger="click" @command="onChangeRole(roles[0], $event)">
          <el-button type="success" :icon="Plus">Add Employee</el-button>
          <template #dropdown>
            <div class="permissions-page__dropdown-search">
              <el-input
                :prefix-icon="Search"
                v-model="searchDropdownQuery"
                placeholder="Name or Email"
                size="small"
              />
            </div>
            <el-dropdown-menu>
              <el-dropdown-item
                v-for="user in filteredDropdownList"
                :key="user.id"
                :command="user.id"
                >{{ user.firstName }} {{ user.lastName }}</el-dropdown-item
              >
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>
    </div>
    <div class="permissions-page__main">
      <div class="permissions-page__content">
        <el-table
          :data="filteredList"
          height="100%"
          style="width: 100%; border-radius: 4px"
        >
          <template #empty>
            <EmptyData class="permissions-page__table-empty" />
          </template>
          <el-table-column prop="id" label="#" width="70">
            <template #default="scope">
              {{ scope.$index + 1 }}
            </template>
          </el-table-column>
          <el-table-column prop="firstName" label="Name">
            <template #default="scope">
              {{ scope.row.firstName }}
              {{ scope.row.lastName }}
            </template>
          </el-table-column>
          <el-table-column prop="email" label="Email" />
          <el-table-column prop="roles" label="Role" width="160">
            <template #default="scope">
              <el-select
                :model-value="scope.row.roles"
                multiple
                class="m-2"
                placeholder="Select"
                size="small"
                @change="onChangeRole($event, scope.row.id)"
              >
                <el-option
                  v-for="item in roles"
                  :key="item"
                  :label="item"
                  :value="item"
                />
              </el-select>
            </template>
          </el-table-column>
          <el-table-column label="" width="86">
            <template #default="scope">
              <el-button
                size="small"
                type="danger"
                plain
                @click="handleDeleteClick(scope.row.id)"
                >Delete</el-button
              >
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { Ref } from "vue";
import { Plus, Search } from "@element-plus/icons-vue";
import { useStore } from "vuex";
import { ElMessageBox } from "element-plus";
import { EmptyData } from "@/shared/ui/components/EmptyData";
import { IUser } from "@/shared/types/permissions";

const store = useStore();
const searchQuery = ref("");
const searchDropdownQuery = ref("");

onMounted(() => {
  store.dispatch("permissions/getUsers");
  store.dispatch("permissions/getRoles");
});

const list: Ref<IUser[]> = computed(() => {
  return store.state.permissions.users;
});

const filteredList = computed(() => {
  if (!searchQuery.value) {
    return list.value.filter((item: IUser) => item.roles?.length);
  }
  return list.value
    .filter((item: IUser) => item.roles?.length)
    .filter((item: IUser) =>
      (item.firstName + item.lastName)
        .toLowerCase()
        .includes(searchQuery.value.toLowerCase())
    );
});

const filteredDropdownList = computed(() => {
  if (!searchDropdownQuery.value) {
    return list.value;
  }
  return list.value.filter(
    (item: IUser) =>
      (item.firstName + item.lastName)
        .toLowerCase()
        .includes(searchDropdownQuery.value.toLowerCase()) ||
      item.email.toLowerCase().includes(searchDropdownQuery.value.toLowerCase())
  );
});

const roles = computed(() => {
  return store.state.permissions.roles;
});

const handleDeleteClick = (id: number) => {
  ElMessageBox.confirm(
    "",
    "Please confirm that you want to delete this user?",
    {
      confirmButtonText: "OK",
      cancelButtonText: "Cancel",
      cancelButtonClass: "cancel-button",
      confirmButtonClass: "confirm-button",
      showClose: false,
    }
  ).then(async () => {
    const formData = new FormData();
    formData.append("roles[]", "");
    store.dispatch("permissions/deleteRoles", id);
  });
};

const onChangeRole = (roles: string[], id: string) => {
  const formData = new FormData();
  roles.forEach((role, index) => {
    formData.append(`roles[${index}]`, role);
  });
  store.dispatch("permissions/updateUser", {
    id,
    field: formData,
  });
};
</script>

<style lang="scss">
.permissions-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  &__top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-right: 24px;
    height: 56px;
    flex-shrink: 0;
    margin-bottom: 16px;
  }
  &__title {
    font-size: 18px;
    font-weight: 800;
    line-height: 26px;
  }
  &__dropdown-search {
    padding: 8px 16px;
  }
  &__top-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  &__main {
    display: flex;
    height: 100%;
    overflow: hidden;
    padding-right: 24px;
  }
  &__content {
    flex-grow: 1;
    height: 100%;
    width: 100%;
    border-radius: var(--Radius-border-radius-base, 4px);
    background: var(--Color-Background-bg-color, #fff);
  }
  &__button-link .el-icon {
    color: #409eff;
  }
  &__table-empty {
    margin: 0 auto;
  }
}
</style>
